/**
 * "Start here" — the five articles a brand-new person reads on day one.
 *
 * These are written for somebody who has never worked in property tax and has
 * never seen this app. Assume nothing. The job of this category is to make the
 * rest of the Help Center readable.
 */

import type { HelpArticle } from '../types';

export const startArticles: HelpArticle[] = [
  {
    slug: 'what-is-appealstack',
    title: 'What does this app actually do?',
    summary: 'AppealStack keeps track of the properties you argue about, the arguments you make, and the money you earn for winning them.',
    category: 'start',
    keywords: ['overview', 'introduction', 'new', 'first day', 'purpose', 'what is this', 'beginner', 'onboarding'],
    related: ['the-big-picture', 'find-your-way-around', 'your-dashboard'],
    body: [
      {
        kind: 'text',
        text: 'Every year, the government decides what each piece of land and each building is worth. That number is called the assessed value. The higher it is, the more property tax the owner has to pay.',
      },
      {
        kind: 'text',
        text: 'Sometimes the government gets that number wrong — too high. When that happens, the owner can argue. That argument is called an appeal. Your firm makes those arguments on behalf of property owners, and gets paid a share of the money it saves them.',
      },
      {
        kind: 'text',
        text: 'That is the whole business, and this app is where it lives. It holds four things:',
      },
      {
        kind: 'bullets',
        items: [
          'Who your clients are, and what they own.',
          'What the government says each property is worth this year.',
          'Every appeal you have going, and what needs to happen next on it.',
          'What you saved each client, and what they owe you for it.',
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'The one sentence version',
        text: 'The government says a building is worth too much. You argue it down. This app tracks the argument and the bill.',
      },
      {
        kind: 'heading',
        text: 'Why this matters more than a spreadsheet',
      },
      {
        kind: 'text',
        text: 'Appeals run on deadlines that do not move. Miss the date to file a protest and the client pays the higher tax for a whole year — there is no appeal of a missed appeal. A spreadsheet will not warn you. This app will.',
      },
      {
        kind: 'text',
        text: 'It also does the math. Working out what a reduction is worth involves tax rates that differ for every school district, city and county that touches the property. Doing that by hand is where firms lose money, either by under-billing or by billing a client for a saving that was not real.',
      },
    ],
  },

  {
    slug: 'find-your-way-around',
    title: 'Finding your way around the screen',
    summary: 'The menu on the left, the bar across the top, and the trail that tells you where you are.',
    category: 'start',
    keywords: ['navigation', 'menu', 'sidebar', 'layout', 'lost', 'where am i', 'breadcrumb', 'top bar', 'ui', 'interface'],
    related: ['searching-for-anything', 'your-dashboard', 'what-is-appealstack'],
    body: [
      {
        kind: 'text',
        text: 'Every screen in the app has the same three parts. Once you know them you can never really get lost.',
      },
      {
        kind: 'shot',
        src: '/help-shots/dashboard.png',
        alt: 'The AppealStack dashboard, showing the dark menu down the left side, a thin bar across the top with a search box and bell icon, and the main content area in the middle.',
        caption: 'The dark strip on the left is the menu. The thin strip on top is the tool bar. Everything else is the page you are on.',
      },
      {
        kind: 'heading',
        text: '1. The menu, down the left',
      },
      {
        kind: 'text',
        text: 'This is how you get everywhere. It is grouped into sections with small gray headings — Workqueue, Portfolio, Tax data, Billing, and so on. Click any item to go to that screen.',
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'Your menu may be shorter than a colleague’s',
        text: 'The app only shows you the things you are allowed to use. If somebody describes a menu item you cannot see, you have not broken anything — you just do not have permission for it. Ask an admin.',
      },
      {
        kind: 'heading',
        text: '2. The bar across the top',
      },
      {
        kind: 'bullets',
        items: [
          'Search — click it, or press Command-K on a Mac (Control-K on Windows), to jump to anything by name.',
          'The bell — new things that need your attention.',
          'The question mark — help for the screen you are looking at right now.',
          'The door icon on the far right — sign out.',
        ],
      },
      {
        kind: 'heading',
        text: '3. The trail at the top left',
      },
      {
        kind: 'text',
        text: 'Just under the top bar you will see something like Clients › Bluebonnet Property Group › 400 Congress Ave. That is called a breadcrumb trail. It shows where you are and how you got there. Every part of it is clickable, so it is the fastest way back up a level.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        text: 'Your name is at the very bottom of the left menu. It shows your role too, which is worth knowing when you are wondering why you cannot see something.',
      },
    ],
  },

  {
    slug: 'your-dashboard',
    title: 'Your dashboard, explained box by box',
    summary: 'The first screen you see after signing in, and what each number on it is actually counting.',
    category: 'start',
    keywords: ['home', 'home page', 'landing', 'first screen', 'numbers', 'stats', 'kpi', 'pipeline', 'chart'],
    related: ['find-your-way-around', 'the-appeals-queue', 'the-calendar'],
    body: [
      {
        kind: 'text',
        text: 'The dashboard is your launchpad. It answers one question: what should I be doing today?',
      },
      {
        kind: 'shot',
        src: '/help-shots/dashboard.png',
        alt: 'The dashboard, with a row of four number cards across the top, a doughnut chart called Appeal pipeline on the left, and a list called Upcoming events on the right.',
      },
      {
        kind: 'heading',
        text: 'The row of numbers at the top',
      },
      {
        kind: 'defs',
        items: [
          { term: 'Parcels under appeal', meaning: 'How many pieces of property you are currently arguing about. This is your live workload.' },
          { term: 'Pending tax bills', meaning: 'Bills that have arrived but nobody has dealt with yet.' },
          { term: 'Billable items', meaning: 'Work you have finished that has not been invoiced. This is money you have earned but not yet asked for.' },
          { term: 'Properties in setup', meaning: 'Properties somebody started adding but did not finish. They will not appear in your appeal work until they are completed.' },
        ],
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'These numbers are clickable',
        text: 'Do not just read them. Clicking a card takes you to the list behind it, already filtered. It is the quickest way into your own work.',
      },
      {
        kind: 'heading',
        text: 'The Appeal pipeline doughnut',
      },
      {
        kind: 'text',
        text: 'This splits every parcel into three states, so you can see the shape of your year at a glance.',
      },
      {
        kind: 'defs',
        items: [
          { term: 'Not filed', meaning: 'You have not argued about these yet. Early in the season this is a big slice, and it should shrink as deadlines approach. If it is still big when a deadline is close, that is a problem.' },
          { term: 'Under appeal', meaning: 'The argument is live and in progress.' },
          { term: 'Settled', meaning: 'It is finished. A value was agreed or decided, and it is ready to bill.' },
        ],
      },
      {
        kind: 'heading',
        text: 'Upcoming events',
      },
      {
        kind: 'text',
        text: 'The next things with a date on them — deadlines to file, hearings to attend, reminders somebody set. Red ones are deadlines. Treat the red ones as immovable.',
      },
      {
        kind: 'callout',
        tone: 'warn',
        title: 'What the dashboard counts depends on who you are',
        text: 'If you are an agent with your own book of clients, these numbers cover your book only, not the whole firm. Two people can look at the same dashboard on the same day and honestly see different numbers. Neither is broken.',
      },
    ],
  },

  {
    slug: 'searching-for-anything',
    title: 'Finding a client, property or parcel fast',
    summary: 'Press Command-K and start typing. It is almost always faster than clicking through menus.',
    category: 'start',
    keywords: ['search', 'command bar', 'cmd k', 'ctrl k', 'find', 'lookup', 'jump', 'quick', 'shortcut', 'keyboard'],
    related: ['find-your-way-around', 'saved-views'],
    body: [
      {
        kind: 'text',
        text: 'You do not have to remember which menu a thing lives under. Search finds it by name.',
      },
      {
        kind: 'steps',
        intro: 'To search from anywhere in the app:',
        steps: [
          'Press Command-K on a Mac, or Control-K on Windows. You can also click the Search box in the top bar.',
          'Start typing a name — a client, a property address, a parcel number, a person.',
          'Results appear as you type, grouped by what kind of thing they are.',
          'Use the up and down arrow keys to pick one, then press Enter. Or just click it.',
        ],
      },
      {
        kind: 'shot',
        src: '/help-shots/command-bar.png',
        alt: 'The search bar open over the dashboard, with a typed query and a list of matching results grouped by type underneath it.',
      },
      {
        kind: 'callout',
        tone: 'tip',
        title: 'Partial words are fine',
        text: 'You do not need the whole name or the exact spelling of an address. Typing "congre" will find 400 Congress Avenue. Typing part of an account number works too.',
      },
      {
        kind: 'text',
        text: 'Press Escape to close the search without going anywhere.',
      },
    ],
  },

  {
    slug: 'getting-help-in-the-app',
    title: 'Getting help without leaving your work',
    summary: 'The question mark buttons dotted around the app explain the thing you are looking at, right where you are looking at it.',
    category: 'start',
    keywords: ['help', 'support', 'question mark', 'tooltip', 'stuck', 'confused', 'documentation', 'guide', 'tour', 'training'],
    related: ['find-your-way-around', 'what-is-appealstack'],
    body: [
      {
        kind: 'text',
        text: 'There are three ways to get an answer, and you should almost never need to leave the screen you are on.',
      },
      {
        kind: 'heading',
        text: '1. The question mark next to a heading or a field',
      },
      {
        kind: 'text',
        text: 'When a word on screen is doing something non-obvious, there is a small question mark beside it. Click it and you get a short plain-language explanation in a little panel. If there is more to say, that panel has a "Read the full guide" link at the bottom.',
      },
      {
        kind: 'shot',
        src: '/help-shots/help-tip.png',
        alt: 'A small question mark icon beside a page heading, with an open panel underneath it explaining the term in two sentences and a link to read more.',
      },
      {
        kind: 'heading',
        text: '2. The Help button in the top bar',
      },
      {
        kind: 'text',
        text: 'This opens the Help Center at whatever is relevant to your current screen. If you are on the Invoices screen, it opens the billing guides.',
      },
      {
        kind: 'heading',
        text: '3. Search the Help Center',
      },
      {
        kind: 'text',
        text: 'Go to Help and type your question in plain words. You do not need the official term. Searching "who owns the building" finds the article about Clients. Searching "why can I not see the money" finds the article about roles and permissions.',
      },
      {
        kind: 'callout',
        tone: 'note',
        title: 'You can redo the welcome tour any time',
        text: 'The guided tour you saw on your first sign-in is not gone. Open Help and click "Take the tour again" at the top of the page.',
      },
    ],
  },
];
